import clsx from 'clsx'
import SelectBranchWorkSpace from 'context/SelectBranchWorkSpace'
import { useStore } from 'context/store'
import { WorkspaceContext } from 'context/workspace.context'
import { observer } from 'mobx-react'
import { Avatar } from 'primereact/avatar'
import { Badge } from 'primereact/badge'
import { Dialog } from 'primereact/dialog'
import { InputSwitch } from 'primereact/inputswitch'
import { Menu } from 'primereact/menu'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { getSelectedKey, isBranchPage } from 'routers/routes'
import { REACT_APP_BRANCH_PREFIX } from 'utils/constants/environment'
import { useIsMobile } from 'utils/hooks/useIsMobile'
import { isHeaquarter } from 'utils/constants/user'

function LeftSideBar({
  collapse,
  onClose,
}: {
  collapse: boolean
  onClose?: () => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [isProfileExpanded, setIsProfileExpanded] = useState(false)
  const [showSelectBranch, setShowSelectBranch] = useState(false)

  const { appRouters, user } = useContext(WorkspaceContext)
  const location = useLocation()
  const activeRoutes = getSelectedKey(appRouters, location)
  const logoSrc = useMemo(() => {
    if (window.location.origin.includes(REACT_APP_BRANCH_PREFIX))
      return '/images/logo-branch.svg'
    else return '/images/logo-app.svg'
  }, [])
  const { i18n } = useTranslation()
  const {
    authStore: { logout },
    contactStore: { hasNewMessages },
  } = useStore()
  useEffect(() => {
    localStorage.setItem('lang', i18n.language)
    document.documentElement.style.setProperty(
      '--font-family',
      i18n.language !== 'jp' ? 'Poppins' : 'Noto Sans'
    )
  }, [i18n.language])
  const itemRenderer = (item) => {
    const isActive = activeRoutes?.map((e) => e.route.key).includes(item.key)
    return (
      <Link className="p-menuitem-content p-menuitem-link" to={item.path}>
        <div className="flex align-items-center p-menuitem-link py-3 relative">
          <i
            className={clsx(
              item.icon,
              ' transition-duration-300 text-xl',
              isActive ? 'text-yellow-500 font-bold' : ''
            )}
          />

          <span
            className={clsx(
              'mx-2  transition-duration-300',
              isActive ? 'text-primary font-bold' : ''
            )}
          >
            {t(item.label)}
          </span>
          {item.badge && <Badge className="ml-auto" value={item.badge} />}
          {item.shortcut && (
            <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">
              {item.shortcut}
            </span>
          )}
          {item.key === 'contact-management' && hasNewMessages ? (
            <div className="pulse red "></div>
          ) : null}
        </div>
      </Link>
    )
  }
  let items = [
    {
      template: () => {
        return (
          <div className="flex align-items-center justify-content-between px-3 md:px-4 md:py-3 py-1 gap-2">
            <Link
              className={clsx(
                'inline-flex align-items-center gap-1',
                isMobile ? '' : 'w-full'
              )}
              to={'/'}
            >
              <img
                src={logoSrc}
                alt="logo"
                className={clsx('max-w-full', isMobile ? 'w-8rem' : 'w-full')}
              />
            </Link>
            {isMobile ? (
              <div onClick={onClose} className="p-2">
                <i className="isax isax-close-01 text-xl"></i>
              </div>
            ) : null}
          </div>
        )
      },
    },
    ...appRouters
      .filter((e) => !e.hiddenFromMenu)
      .map((route) => {
        return {
          ...route,
          template: itemRenderer,
        }
      }),
    // {
    //   template: () => {
    //     return REACT_APP_PARENT_APP_DOMAIN.includes('dev') ? (
    //       <div className="flex gap-2 align-items-center px-4">
    //         English
    //         <InputSwitch
    //           checked={i18n.language !== 'jp'}
    //           onChange={() => {
    //             const lang = i18n.language !== 'jp' ? 'jp' : 'en'
    //             i18n.changeLanguage(lang)
    //             localStorage.setItem('lang', lang)
    //           }}
    //         />
    //       </div>
    //     ) : null
    //   },
    // },
  ]
  return (
    <div
      className={clsx(
        'card flex flex-column h-full justify-content-center transition-duration-300 overflow-hidden flex-nowrap sticky top-0 '
      )}
      style={{
        flex: collapse ? '0 0 0' : '0 0 14rem',
        minHeight: '100vh',
      }}
    >
      <Menu
        model={items}
        className="w-full min-w-min border-0 border-noround flex-1"
      />
      <div className="px-4 py-3 bg-white border-top-1 surface-border">
        {isMobile ? (
          <>
            <div
              className="flex align-items-center justify-content-between gap-2 mb-2 cursor-pointer"
              onClick={() => setIsProfileExpanded((prev) => !prev)}
            >
              <div className="flex align-items-center gap-2 min-w-0">
                <Avatar
                  className="bg-light-primary text-white opacity-1"
                  label={user.fullName?.slice(0, 1)}
                  shape="circle"
                />
                <div className="overflow-hidden">
                  <p className="m-0 font-semibold text-sm overflow-hidden text-overflow-ellipsis white-space-nowrap">
                    {user.fullName}
                  </p>
                  <p className="m-0 text-xs text-color-secondary overflow-hidden text-overflow-ellipsis white-space-nowrap">
                    {user.email}
                  </p>
                </div>
              </div>
              <i
                className={clsx(
                  'isax-arrow-down-1 text-sm transition-duration-200',
                  isProfileExpanded ? 'rotate-180' : ''
                )}
              />
            </div>
            <div
              className={clsx(
                'overflow-hidden transition-all transition-duration-200',
                isProfileExpanded
                  ? 'max-h-9rem opacity-100'
                  : 'max-h-0 opacity-0'
              )}
            >
              <div className="flex flex-column gap-1 min-w-8rem">
                <Link
                  to="/profile"
                  className="flex align-items-center gap-2 py-2 text-gray-700"
                  onClick={onClose}
                >
                  <i className="isax-profile-circle" />
                  <span>{t('Profile')}</span>
                </Link>
                {isBranchPage && isHeaquarter(user) ? (
                  <div
                    className="flex align-items-center gap-2 py-2 cursor-pointer text-gray-700"
                    onClick={() => setShowSelectBranch(true)}
                  >
                    <i className="isax-programming-arrow" />
                    <span>{t('Go to branch page')}</span>
                  </div>
                ) : null}
                <div
                  className="flex align-items-center gap-2 py-2 cursor-pointer text-gray-700"
                  onClick={logout}
                >
                  <i className="isax-logout" />
                  <span>{t('Sign out')}</span>
                </div>
              </div>
            </div>
          </>
        ) : null}
        {process.env.REACT_APP_PARENT_APP_DOMAIN.includes('dev') ? (
          <div className="flex gap-2 align-items-center pt-2">
            English
            <InputSwitch
              checked={i18n.language !== 'jp'}
              onChange={() => {
                const lang = i18n.language !== 'jp' ? 'jp' : 'en'
                i18n.changeLanguage(lang)
              }}
            />
          </div>
        ) : null}
      </div>
      <Dialog
        visible={showSelectBranch}
        onHide={() => setShowSelectBranch(false)}
        showHeader={false}
        closable={false}
        draggable={false}
        resizable={false}
        modal
        className="mobile-select-branch-dialog m-0 shadow-none border-noround"
        contentClassName="p-0 h-full"
        style={{
          width: '100vw',
          maxWidth: '100vw',
          height: '100vh',
          maxHeight: '100vh',
        }}
      >
        <div className="w-full h-full  p-3 md:p-4 relative flex align-items-center justify-content-center">
          <div
            onClick={() => setShowSelectBranch(false)}
            className="absolute top-0 right-0 mr-3 mt-3 p-2 z-2"
            aria-label={t('Close')}
          >
            <i className="isax isax-close-01 text-xl"></i>
          </div>

          <SelectBranchWorkSpace
            className="w-full max-w-30rem py-3 md:py-4"
            showLogo={false}
          />
        </div>
      </Dialog>
    </div>
  )
}
export default observer(LeftSideBar)
